/* Generic "common" dictionary blocks – supports all OpenFOAM parser constructs */
import * as Blockly from 'blockly';

/* --- basics ------------------------------------------------------------ */

export const dictName = 'common';
const PREFIX = 'common';
const BLOCK_COLOR = 110; // unified palette
const TUPLE_COLOR = 160; // distinct color for tuples
const SPECIAL_COLOR = 190; // for macros, codestreams, etc.

/* --- value blocks ------------------------------------------------------ */

function registerInvalidValueBlock() {
  const id = `${PREFIX}_value_invalid`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.jsonInit({
        type: id,
        message0: 'Invalid: %1',
        args0: [
          { type: 'field_input', name: 'VALUE', text: '' }
        ],
        output: null, // Allow connection to any type
        colour: '#FF6B6B', // Error color (red)
        tooltip: 'Invalid value that needs correction',
      });
      
      // Add warning text
      this.setWarningText('This value is invalid. Please replace it with a valid value.');
    },
    
    // Override onchange to update warning text if value changes
    onchange: function() {
      const value = this.getFieldValue('VALUE');
      this.setWarningText(`Invalid value: "${value}". Please replace with a valid value.`);
    }
  };
  
  console.log(`Registered invalid value block: ${id}`);
}

function registerValueBlocks() {
  const defs = [
    {
      id: `${PREFIX}_value_string`,
      output: 'String',
      init() {
        this.jsonInit({
          type: `${PREFIX}_value_string`,
          message0: '❝ %1 ❞',
          args0: [{ type: 'field_input', name: 'VALUE', text: '' }],
          output: 'String',
          colour: BLOCK_COLOR,
          tooltip: 'String value (e.g., "text")',
        });
      },
    },
    {
      id: `${PREFIX}_value_number`,
      output: 'Number',
      init() {
        this.jsonInit({
          type: `${PREFIX}_value_number`,
          message0: '%1',
          args0: [{ type: 'field_number', name: 'VALUE', value: 0 }],
          output: 'Number',
          colour: BLOCK_COLOR,
          tooltip: 'Integer or floating-point number',
        });
      },
    },
    {
      id: `${PREFIX}_value_boolean`,
      output: 'Boolean',
      init() {
        this.jsonInit({
          type: `${PREFIX}_value_boolean`,
          message0: 'Boolean %1',
          args0: [
            {
              type: 'field_dropdown',
              name: 'VALUE',
              options: [
                ['true', 'true'],
                ['false', 'false'],
              ],
            },
          ],
          output: 'Boolean',
          colour: BLOCK_COLOR,
          tooltip: 'Boolean value (true/false)',
        });
        this.setFieldValue('true', 'VALUE');
      },
    },
    {
      id: `${PREFIX}_value_substitution`,
      output: 'Substitution',
      init() {
        this.jsonInit({
          type: `${PREFIX}_value_substitution`,
          message0: 'Substitution %1',
          args0: [{ type: 'field_input', name: 'VALUE', text: '$var' }],
          output: 'Substitution',
          colour: BLOCK_COLOR,
          tooltip: 'Substitution variable (e.g., $var)',
        });
      },
    },
    {
      id: `${PREFIX}_value_vector3`,
      output: 'Vector3',
      init() {
        this.jsonInit({
          type: `${PREFIX}_value_vector3`,
          message0: 'Vector (%1 %2 %3)',
          args0: [
            { type: 'field_number', name: 'X', value: 0 },
            { type: 'field_number', name: 'Y', value: 0 },
            { type: 'field_number', name: 'Z', value: 0 },
          ],
          output: 'Vector3',
          colour: BLOCK_COLOR,
          tooltip: '3D vector (x, y, z)',
        });
      },
    },
    {
      id: `${PREFIX}_value_connectivity4`,
      output: 'Connectivity4',
      init() {
        this.jsonInit({
          type: `${PREFIX}_value_connectivity4`,
          message0: 'Connectivity (%1 %2 %3 %4)',
          args0: [
            { type: 'field_number', name: 'V0', value: 0, precision: 0 },
            { type: 'field_number', name: 'V1', value: 1, precision: 0 },
            { type: 'field_number', name: 'V2', value: 2, precision: 0 },
            { type: 'field_number', name: 'V3', value: 3, precision: 0 },
          ],
          output: 'Connectivity4',
          colour: BLOCK_COLOR,
          tooltip: 'Connectivity4 value (v0, v1, v2, v3)',
        });
      },
    },
  ];

  defs.forEach(({ id, init }) => {
    if (!Blockly.Blocks[id]) {
      Blockly.Blocks[id] = { init };
    }
  });
  
  // Register the invalid value block
  registerInvalidValueBlock();
}

/* --- vector blocks (NEW) ---------------------------------------------- */

function registerVectorBlocks() {

  // Register common_vector3 block
  const vector3Id = `${PREFIX}_vector3`;
  if (!Blockly.Blocks[vector3Id]) {
    Blockly.Blocks[vector3Id] = {
      init() {
        this.appendDummyInput().appendField('Vector');
        this.appendValueInput('X')
          .setCheck('Number')          
          .appendField('X');
        this.appendValueInput('Y')
          .setCheck('Number')
          .appendField('Y');
        this.appendValueInput('Z')
          .setCheck('Number')
          .appendField('Z')
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour(BLOCK_COLOR);
        this.setTooltip('A 3D vector with x, y, z components');
        this.setHelpUrl('');
      },
    };
    console.log(`Registered vector3 block: ${vector3Id}`);
  }

  // Register common_vector3List block
  const vector3ListId = `${PREFIX}_vector3list`;
  if (!Blockly.Blocks[vector3ListId]) {
    Blockly.Blocks[vector3ListId] = {
      init() {
        this.appendDummyInput()
          .appendField('Vector List')
          .appendField(new Blockly.FieldTextInput('vertices'), 'LIST_NAME');
        this.appendStatementInput('VECTORS')
          .setCheck('Vector3')
          .appendField('vectors');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour(BLOCK_COLOR);
        this.setTooltip('A named List of 3D vectors');
        this.setHelpUrl('');
      },
    };
    console.log(`Registered vector3list block: ${vector3ListId}`);
  }

  // Register connectivity4 block
  const connectivity4Id = `${PREFIX}_connectivity4`;
  if (!Blockly.Blocks[connectivity4Id]) {
    Blockly.Blocks[connectivity4Id] = {
      init() {
        this.appendDummyInput().appendField('Connectivity');
        this.appendValueInput('V0')
          .setCheck('Number')          
          .appendField('v0');
        this.appendValueInput('V1')
          .setCheck('Number')
          .appendField('v1');
        this.appendValueInput('V2')
          .setCheck('Number')
          .appendField('v2');
        this.appendValueInput('V3')
          .setCheck('Number')
          .appendField('v3');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour(BLOCK_COLOR);
        this.setTooltip('A connectivity tuple with 4 vertex indices');
        this.setHelpUrl('');
      },
    };
    console.log(`Registered connectivity4 block: ${connectivity4Id}`);
  }

  // Register common_connectivity4List block
  const connectivity4ListId = `${PREFIX}_connectivity4list`;
  if (!Blockly.Blocks[connectivity4ListId]) {
    Blockly.Blocks[connectivity4ListId] = {
      init() {
        this.appendDummyInput()
          .appendField('Connectivity List')
          .appendField(new Blockly.FieldTextInput('faces'), 'LIST_NAME');
        this.appendStatementInput('CONNECTIONS')
          .setCheck('Connectivity4')
          .appendField('connections');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour(BLOCK_COLOR);
        this.setTooltip('A named List of connectivity tuples');
        this.setHelpUrl('');
      },
    };
    console.log(`Registered connectivity4list block: ${connectivity4ListId}`);
  }
}

/* --- reusable dictionary entry block ---------------------------------- */

function registerEntryBlock() {
  const id = `${PREFIX}_entry`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.jsonInit({
        type: id,
        message0: 'Entry %1 %2',
        args0: [
          { type: 'field_input', name: 'KEY', text: 'name' },
          {
            type: 'input_value',
            name: 'VALUE',
            check: [
              'String',
              'Number',
              'Boolean',
              'Substitution',
              'ValueList',
              'Dictionary',
              'FieldValue',
              'SymbolicDimension',
              'List',
              'Vector3',
              'Connectivity4',
              'ValueList',
              'FunctionCall',
            ],
          },
        ],
        previousStatement: null,
        nextStatement: null,
        colour: BLOCK_COLOR,
        tooltip: 'Key–value pair supporting various value types',
      });
    },
  };
}

/* --- predefined FoamFile dictionary block ----------------------------- */

function registerFoamFileDictBlock() {
  const id = `${PREFIX}_foamfile_dict`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.appendDummyInput().appendField('FoamFile');
      this.appendValueInput('VERSION')
        .setCheck('String')
        .appendField('version');
      this.appendValueInput('FORMAT')
        .setCheck('String')
        .appendField('format');
      this.appendValueInput('CLASS')
        .setCheck('String')
        .appendField('class');
      this.appendValueInput('LOCATION')
        .setCheck('String')
        .appendField('location');
      this.appendValueInput('OBJECT')
        .setCheck('String')
        .appendField('object');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(BLOCK_COLOR);
      this.setTooltip('FoamFile dictionary with predefined entries');
      this.setHelpUrl('');
    },
  };
}

/* --- generic dictionary block ----------------------------------------- */

function registerRootDictBlock() {
  const id = `${PREFIX}_dict`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.appendDummyInput()
        .appendField('Dictionary')
        .appendField(new Blockly.FieldTextInput('name'), 'DICT_NAME');
      this.appendStatementInput('ENTRIES')
        .setCheck(null)
        .appendField('entries');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(BLOCK_COLOR);
      this.setTooltip('Generic dictionary (can be nested)');
    },
  };
}

/* --- block section block --------------------------------------------- */

function registerBlockSectionBlock() {
  const id = `${PREFIX}_block_section`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.appendDummyInput()
        .appendField('List')
        .appendField(new Blockly.FieldTextInput('name'), 'SECTION_NAME');
      this.appendStatementInput('ENTRIES').setCheck(null);
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(BLOCK_COLOR);
      this.setTooltip('Named List with entries');
      this.setHelpUrl('');
    },
  };

  Blockly.Blocks[`${PREFIX}_block_entry`] = {
    init() {
      this.appendValueInput('VALUE')
        .setCheck(['String', 'Number', 'Name'])
        .appendField('entry');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(BLOCK_COLOR);
      this.setTooltip('Entry in a block section');
      this.setHelpUrl('');
    },
  };
}

/* --- macro block ----------------------------------------------------- */

function registerMacroBlock() {
  const id = `${PREFIX}_macro`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.appendDummyInput()
        .appendField('#')
        .appendField(
          new Blockly.FieldDropdown([
            ['include', 'include'],
            ['includeEtc', 'includeEtc'],
            ['includeFunc', 'includeFunc'],
            ['includeIfPresent', 'includeIfPresent'],
            ['remove', 'remove'],
            ['inputMode', 'inputMode'],
          ]),
          'DIRECTIVE'
        );
      this.appendValueInput('ARGUMENT')
        .setCheck('String')
        .appendField('argument');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(SPECIAL_COLOR);
      this.setTooltip('Macro directive (e.g., #include "file")');
      this.setHelpUrl('');
    },
  };
}

/* --- codestream block ----------------------------------------------- */

function registerCodestreamBlock() {
  const id = `${PREFIX}_codestream`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.appendDummyInput().appendField('#{');
      this.appendDummyInput()
        .appendField('code')
        .appendField(new Blockly.FieldTextInput(''), 'CONTENT');
      this.appendDummyInput().appendField('#}');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(SPECIAL_COLOR);
      this.setTooltip('Codestream block for embedded code');
      this.setHelpUrl('');
    },
  };
}

/* --- function object block ------------------------------------------- */

function registerFunctionObjectBlock() {
  const id = `${PREFIX}_function_object`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.appendDummyInput()
        .appendField('Function Object')
        .appendField(new Blockly.FieldTextInput('name'), 'FUNC_NAME');
      this.appendValueInput('TYPE')
        .setCheck('String')
        .appendField('type');
      this.appendStatementInput('ENTRIES')
        .setCheck(null)
        .appendField('entries');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(BLOCK_COLOR);
      this.setTooltip('Function object with type and key-value entries');
      this.setHelpUrl('');
    },
  };
}

/* --- field value blocks --------------------------------------------- */

function registerFieldValueBlocks() {
  const uniformId = `${PREFIX}_fieldvalue_uniform`;
  const nonuniformId = `${PREFIX}_fieldvalue_nonuniform`;

  Blockly.Blocks[uniformId] = {
    init() {
      this.appendDummyInput().appendField('uniform');
      this.appendValueInput('VALUE').setCheck([
        'String',
        'Number',
        'List',
        'Dictionary',
      ]);
      this.setOutput(true, 'FieldValue');
      this.setColour(BLOCK_COLOR);
      this.setTooltip('Uniform field value');
      this.setHelpUrl('');
    },
  };

  Blockly.Blocks[nonuniformId] = {
    init() {
      this.appendDummyInput().appendField('nonuniform');
      this.appendValueInput('TYPE')
        .setCheck('String')
        .appendField('type')
        .appendField('(optional)');
      this.appendValueInput('VALUE').setCheck(['List', 'PreList']);
      this.setOutput(true, 'FieldValue');
      this.setColour(BLOCK_COLOR);
      this.setTooltip('Nonuniform field value with optional type');
      this.setHelpUrl('');
    },
  };
}

/* --- common_list block ----------------------------------------------------- */

function registerListBlock() {
  const id = `${PREFIX}_list`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.appendDummyInput()
        .appendField('List Name')
        .appendField(new Blockly.FieldTextInput('name'), 'LIST_NAME');
      this.appendDummyInput().appendField('(');
      this.appendStatementInput('ITEMS').setCheck(null);
      this.appendDummyInput().appendField(')');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(BLOCK_COLOR);
      this.setTooltip('List of values');
      this.setHelpUrl('');
    },
  };

  Blockly.Blocks[`${PREFIX}_list_item`] = {
    init() {
      this.appendValueInput('VALUE')
        .setCheck(['String', 'Number', 'Name', 'List'])
        .appendField('item');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(BLOCK_COLOR);
      this.setTooltip('Item in a List');
      this.setHelpUrl('');
    },
  };
}

/* --- preList block -------------------------------------------------- */

function registerPreListBlock() {
  const id = `${PREFIX}_pre_list`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.appendValueInput('SIZE')
        .setCheck('Number')
        .appendField('size');
      this.appendValueInput('CONTENT')
        .setCheck(['List', 'String'])
        .appendField('content');
      this.setOutput(true, 'PreList');
      this.setColour(BLOCK_COLOR);
      this.setTooltip('Pre List with size and content (List or binary blob)');
      this.setHelpUrl('');
    },
  };
}

/* --- symbolic dimension block --------------------------------------- */

function registerSymbolicDimensionBlock() {
  const id = `${PREFIX}_symbolic_dimension`;
  if (Blockly.Blocks[id]) return;

  Blockly.Blocks[id] = {
    init() {
      this.appendDummyInput().appendField('[');
      this.appendDummyInput()
        .appendField('values')
        .appendField(new Blockly.FieldTextInput('kg m^-2 s^-1'), 'VALUES');
      this.appendDummyInput().appendField(']');
      this.setOutput(true, 'SymbolicDimension');
      this.setColour(BLOCK_COLOR);
      this.setTooltip('Symbolic dimension (e.g., [kg m^-2 s^-1])');
      this.setHelpUrl('');
    },
  };
}

/* --- named dictionary List block -------------------------------------- */

function registerNamedDictListBlock() {
  const blockId = `${PREFIX}_named_dict_list`;
  if (!Blockly.Blocks[blockId]) {
    Blockly.Blocks[blockId] = {
      init() {
        this.appendDummyInput()
          .appendField('Dictionary List')
          .appendField(new Blockly.FieldTextInput('List'), 'LIST_NAME');
        this.appendStatementInput('DICTIONARIES')
          .setCheck('Dictionary')
          .appendField('dictionaries');
        // this.setOutput(true, 'DictionaryList');
        this.setPreviousStatement(true, null);
        this.setNextStatement(true, null);
        this.setColour(BLOCK_COLOR);
        this.setTooltip('A named list of dictionaries');
        this.setHelpUrl('');
      },
    };
    console.log(`Registered namedDictList block: ${blockId}`);
  }
}

/* --- multi-value entry block ------------------------------------------ */

function registerMultiValueEntryBlock() {
  const id = `${PREFIX}_multi_value_entry`;
  if (Blockly.Blocks[id]) return;

  // Main multi-value entry block
  Blockly.Blocks[id] = {
    init() {
      this.appendDummyInput()
        .appendField('Multi-Value Entry')
        .appendField(new Blockly.FieldTextInput('div(phi,U)'), 'KEY');
      this.appendValueInput('VALUES')
        .setCheck('MultiValue')
        .appendField('values');
      this.setPreviousStatement(true, null);
      this.setNextStatement(true, null);
      this.setColour(BLOCK_COLOR);
      this.setTooltip('Multi-value entry with text key and horizontal chain of values (e.g., div(phi,U) bounded Gauss linearUpwindV grad(U))');
      this.setHelpUrl('');
    },
  };

  // Function call value block for complex keys like div(phi,U)
  const functionCallId = `${PREFIX}_function_call`;
  if (!Blockly.Blocks[functionCallId]) {
    Blockly.Blocks[functionCallId] = {
      init() {
        this.appendDummyInput()
          .appendField(new Blockly.FieldTextInput('div'), 'FUNCTION_NAME')
          .appendField('(');
        this.appendValueInput('ARGS')
          .setCheck('String')
          .appendField('arguments');
        this.appendDummyInput()
          .appendField(')');
        this.setOutput(true, 'FunctionCall');
        this.setColour(BLOCK_COLOR);
        this.setTooltip('Function call (e.g., div(phi,U), grad(U))');
        this.setHelpUrl('');
      },
    };
  }

  // Multi-value item block that chains horizontally
  const valueItemId = `${PREFIX}_multi_value_item`;
  if (!Blockly.Blocks[valueItemId]) {
    Blockly.Blocks[valueItemId] = {
      init() {
        this.appendDummyInput()
          .appendField(new Blockly.FieldTextInput('bounded'), 'VALUE');
        this.appendValueInput('NEXT')
          .setCheck('MultiValue')
          .appendField('then');
        this.setOutput(true, 'MultiValue');
        this.setColour(BLOCK_COLOR);
        this.setTooltip('A value in a multi-value entry that chains horizontally with other values');
        this.setHelpUrl('');
      },
    };
  }

  // String arguments block for function calls
  const stringArgsId = `${PREFIX}_string_args`;
  if (!Blockly.Blocks[stringArgsId]) {
    Blockly.Blocks[stringArgsId] = {
      init() {
        this.appendDummyInput()
          .appendField(new Blockly.FieldTextInput('phi,U'), 'ARGS');
        this.setOutput(true, 'String');
        this.setColour(BLOCK_COLOR);
        this.setTooltip('Comma-separated arguments for function calls (e.g., phi,U)');
        this.setHelpUrl('');
      },
    };
  }
}

/* --- value list block -------------------------------------------------- */

function registerValueListBlock() {
  const valueListId = `${PREFIX}_value_list`;
  if (Blockly.Blocks[valueListId]) return;

  // NOTE: The old chaining value list approach (common_value_list/common_value_item) 
  // has been replaced with the multi-part approach for consistency with fvSchemes
  
  // Old blocks commented out - keeping for reference but no longer used
  /*
  // Main value list block - horizontally chained values
  Blockly.Blocks[valueListId] = {
    init() {
      this.appendValueInput('FIRST_VALUE')
        .appendField('Values:')
        .setCheck(['String', 'Number', 'Boolean', 'FunctionCall', 'ValueList']);
      this.appendValueInput('NEXT_VALUE')
        .appendField('+')
        .setCheck('ValueList');
      this.setOutput(true, 'ValueList');
      this.setColour(BLOCK_COLOR);
      this.setTooltip('A list of values (strings, numbers, function calls) that can be chained together');
      this.setHelpUrl('');
    },
  };

  // Single value item that can be part of a value list
  const valueItemId = `${PREFIX}_value_item`;
  if (!Blockly.Blocks[valueItemId]) {
    Blockly.Blocks[valueItemId] = {
      init() {
        this.appendValueInput('VALUE')
          .setCheck(['String', 'Number', 'Boolean', 'FunctionCall', 'ValueList']);
        this.appendValueInput('NEXT')
          .appendField('+')
          .setCheck('ValueList');
        this.setOutput(true, 'ValueList');
        this.setColour(BLOCK_COLOR);
        this.setTooltip('A value that can be chained with other values in a list');
        this.setHelpUrl('');
      },
    };
  }
  */

  // Multi-part value list block (similar to fvSchemes) - for up to 4 values
  const multiPartValueId = `${PREFIX}_multi_part_value`;
  if (!Blockly.Blocks[multiPartValueId]) {
    Blockly.Blocks[multiPartValueId] = {
      init() {
        this.jsonInit({
          type: multiPartValueId,
          message0: '%1 %2 %3 %4',
          args0: [
            {
              type: 'input_value',
              name: 'PART1',
              check: ['String', 'Number', 'Boolean']
            },
            {
              type: 'input_value',
              name: 'PART2',
              check: ['String', 'Number', 'Boolean']
            },
            {
              type: 'input_value',
              name: 'PART3',
              check: ['String', 'Number', 'Boolean']
            },
            {
              type: 'input_value',
              name: 'PART4',
              check: ['String', 'Number', 'Boolean']
            }
          ],
          output: 'ValueList',
          colour: BLOCK_COLOR + 10,
          tooltip: 'Multi-part value list (up to 4 parts, leave unused parts empty)',
          inputsInline: true
        });
      }
    };
  }

  // Function call block for function calls like div(phi,U), grad(U), etc.
  const functionCallId = `${PREFIX}_function_call_value`;
  if (!Blockly.Blocks[functionCallId]) {
    Blockly.Blocks[functionCallId] = {
      init() {
        this.appendDummyInput()
          .appendField(new Blockly.FieldTextInput('div'), 'FUNCTION_NAME')
          .appendField('(');
        this.appendDummyInput()
          .appendField(new Blockly.FieldTextInput('phi,U'), 'ARGUMENTS')
          .appendField(')');
        this.setOutput(true, 'FunctionCall');
        this.setColour(BLOCK_COLOR);
        this.setTooltip('Function call with arguments (e.g., div(phi,U), grad(U))');
        this.setHelpUrl('');
      },
    };
  }
}

/* --- toolbox & helpers ------------------------------------------------ */

function generateToolbox() {
  return `
<xml>
  <block type="${PREFIX}_foamfile_dict">
    <value name="VERSION"><shadow type="${PREFIX}_value_string"><field name="VALUE">2.0</field></shadow></value>
    <value name="FORMAT"><shadow type="${PREFIX}_value_string"><field name="VALUE">ascii</field></shadow></value>
    <value name="CLASS"><shadow type="${PREFIX}_value_string"><field name="VALUE">dictionary</field></shadow></value>
    <value name="LOCATION"><shadow type="${PREFIX}_value_string"><field name="VALUE">"system"</field></shadow></value>
    <value name="OBJECT"><shadow type="${PREFIX}_value_string"><field name="VALUE">controlDict</field></shadow></value>
  </block>

  <block type="${PREFIX}_dict">
    <field name="DICT_NAME">subDict</field>
    <statement name="ENTRIES">
      <block type="${PREFIX}_entry">
        <value name="VALUE">
          <shadow type="${PREFIX}_value_string"><field name="VALUE">value</field></shadow>
        </value>
      </block>
    </statement>
  </block>

  <block type="${PREFIX}_entry">
    <value name="VALUE">
      <shadow type="${PREFIX}_value_string"></shadow>
    </value>
  </block>

  <block type="${PREFIX}_block_section">
    <field name="SECTION_NAME">myBlock</field>
    <statement name="ENTRIES">
      <block type="${PREFIX}_block_entry">
        <value name="VALUE">
          <shadow type="${PREFIX}_value_string"><field name="VALUE">entry1</field></shadow>
        </value>
      </block>
    </statement>
  </block>

  <!-- NEW: Vector3 block -->
  <block type="${PREFIX}_vector3">
    <value name="X">
      <shadow type="${PREFIX}_value_number"><field name="VALUE">0</field></shadow>
    </value>
    <value name="Y">
      <shadow type="${PREFIX}_value_number"><field name="VALUE">0</field></shadow>
    </value>
    <value name="Z">
      <shadow type="${PREFIX}_value_number"><field name="VALUE">0</field></shadow>
    </value>
  </block>

  <!-- NEW: Vector3list block -->
  <block type="${PREFIX}_vector3list">
    <field name="common_list_NAME">vertices</field>
    <statement name="VECTORS">
      <block type="${PREFIX}_vector3">
        <value name="X">
          <shadow type="${PREFIX}_value_number"><field name="VALUE">0</field></shadow>
        </value>
        <value name="Y">
          <shadow type="${PREFIX}_value_number"><field name="VALUE">0</field></shadow>
        </value>
        <value name="Z">
          <shadow type="${PREFIX}_value_number"><field name="VALUE">0</field></shadow>
        </value>
      </block>
    </statement>
  </block>

  <block type="${PREFIX}_macro">
    <value name="ARGUMENT">
      <shadow type="${PREFIX}_value_string"><field name="VALUE">"file"</field></shadow>
    </value>
  </block>

  <block type="${PREFIX}_codestream">
    <field name="CONTENT">code</field>
  </block>

  <block type="${PREFIX}_function_object">
    <field name="FUNC_NAME">myFunction</field>
    <value name="TYPE">
      <shadow type="${PREFIX}_value_string"><field name="VALUE">typeName</field></shadow>
    </value>
    <statement name="ENTRIES">
      <block type="${PREFIX}_entry">
        <value name="VALUE">
          <shadow type="${PREFIX}_value_string"><field name="VALUE">value</field></shadow>
        </value>
      </block>
    </statement>
  </block>

  <block type="${PREFIX}_fieldvalue_uniform">
    <value name="VALUE">
      <shadow type="${PREFIX}_value_number"><field name="VALUE">0</field></shadow>
    </value>
  </block>

  <block type="${PREFIX}_fieldvalue_nonuniform">
    <value name="TYPE">
      <shadow type="${PREFIX}_value_string"><field name="VALUE">common_list</field></shadow>
    </value>
    <value name="VALUE">
      <block type="${PREFIX}_list">
        <statement name="ITEMS">
          <block type="${PREFIX}_list_item">
            <value name="VALUE">
              <shadow type="${PREFIX}_value_number"><field name="VALUE">0</field></shadow>
            </value>
          </block>
        </statement>
      </block>
    </value>
  </block>

  <block type="${PREFIX}_list">
    <statement name="ITEMS">
      <block type="${PREFIX}_list_item">
        <value name="VALUE">
          <shadow type="${PREFIX}_value_number"><field name="VALUE">0</field></shadow>
        </value>
      </block>
    </statement>
  </block>

  <block type="${PREFIX}_pre_list">
    <value name="SIZE">
      <shadow type="${PREFIX}_value_number"><field name="VALUE">10</field></shadow>
    </value>
    <value name="CONTENT">
      <block type="${PREFIX}_list">
        <statement name="ITEMS">
          <block type="${PREFIX}_list_item">
            <value name="VALUE">
              <shadow type="${PREFIX}_value_number"><field name="VALUE">0</field></shadow>
            </value>
          </block>
        </statement>
      </block>
    </value>
  </block>

  <block type="${PREFIX}_symbolic_dimension">
    <field name="VALUES">kg m^-2 s^-1</field>
  </block>

  <block type="${PREFIX}_multi_value_entry">
    <value name="KEY">
      <shadow type="${PREFIX}_function_call">
        <field name="FUNCTION_NAME">div</field>
        <value name="ARGS">
          <shadow type="${PREFIX}_string_args">
            <field name="ARGS">phi,U</field>
          </shadow>
        </value>
      </shadow>
    </value>
    <statement name="VALUES">
      <block type="${PREFIX}_multi_value_item">
        <value name="VALUE">
          <shadow type="${PREFIX}_value_string">
            <field name="VALUE">bounded</field>
          </shadow>
        </value>
        <next>
          <block type="${PREFIX}_multi_value_item">
            <value name="VALUE">
              <shadow type="${PREFIX}_value_string">
                <field name="VALUE">Gauss</field>
              </shadow>
            </value>
          </block>
        </next>
      </block>
    </statement>
  </block>

  <block type="${PREFIX}_function_call">
    <field name="FUNCTION_NAME">grad</field>
    <value name="ARGS">
      <shadow type="${PREFIX}_string_args">
        <field name="ARGS">U</field>
      </shadow>
    </value>
  </block>

  <block type="${PREFIX}_multi_value_item">
    <value name="VALUE">
      <shadow type="${PREFIX}_value_string">
        <field name="VALUE">value</field>
      </shadow>
    </value>
  </block>

  <sep gap="8"></sep>
  <block type="${PREFIX}_value_string"></block>
  <block type="${PREFIX}_value_number"></block>
  <block type="${PREFIX}_value_boolean"></block>
  <block type="${PREFIX}_value_substitution"></block>
  <block type="${PREFIX}_value_vector3"></block>
  <block type="${PREFIX}_value_connectivity4"></block>
  
  <sep gap="8"></sep>
  <category name="Value Lists" colour="${BLOCK_COLOR}">
    <block type="${PREFIX}_multi_part_value">
      <value name="PART1">
        <shadow type="${PREFIX}_value_string"><field name="VALUE">part1</field></shadow>
      </value>
      <value name="PART2">
        <shadow type="${PREFIX}_value_string"><field name="VALUE">part2</field></shadow>
      </value>
      <value name="PART3">
        <shadow type="${PREFIX}_value_string"><field name="VALUE">part3</field></shadow>
      </value>
    </block>
    <block type="${PREFIX}_function_call_value">
      <field name="FUNCTION_NAME">div</field>
      <field name="ARGUMENTS">phi,U</field>
    </block>
  </category>
</xml>`;
}

function getBlocks() {
  return [
    `${PREFIX}_foamfile_dict`,
    `${PREFIX}_dict`,
    `${PREFIX}_entry`,
    `${PREFIX}_block_section`,
    `${PREFIX}_block_entry`,
    `${PREFIX}_macro`,
    `${PREFIX}_codestream`,
    `${PREFIX}_function_object`,
    `${PREFIX}_fieldvalue_uniform`,
    `${PREFIX}_fieldvalue_nonuniform`,
    `${PREFIX}_list`,
    `${PREFIX}_list_item`,
    `${PREFIX}_pre_list`,
    `${PREFIX}_symbolic_dimension`,
    `${PREFIX}_value_string`,
    `${PREFIX}_value_number`,
    `${PREFIX}_value_boolean`,
    `${PREFIX}_value_substitution`,
    `${PREFIX}_substitution_statement`,
    `${PREFIX}_value_vector3`,
    `${PREFIX}_value_connectivity4`,
    `${PREFIX}_vector3`, 
    `${PREFIX}_vector3list`,
    `${PREFIX}_connectivity4`,
    `${PREFIX}_connectivity4list`,
    `${PREFIX}_named_dict_list`,
    `${PREFIX}_multi_value_entry`,
    `${PREFIX}_function_call`,
    `${PREFIX}_multi_value_item`,
    `${PREFIX}_string_args`,
    `${PREFIX}_multi_part_value`,
    `${PREFIX}_function_call_value`,
  ];
}

/* --- public registrar -------------------------------------------------- */

function registerBlocks() {
  registerValueBlocks();
  registerEntryBlock();
  registerRootDictBlock();
  registerFoamFileDictBlock();
  registerBlockSectionBlock();
  registerMacroBlock();
  registerCodestreamBlock();
  registerFunctionObjectBlock();
  registerFieldValueBlocks();
  registerListBlock();
  registerPreListBlock();
  registerSymbolicDimensionBlock();
  registerVectorBlocks();
  registerNamedDictListBlock();
  registerMultiValueEntryBlock();
  registerValueListBlock();
  registerSubstitutionBlocks();
}

export default {
  dictName,
  registerBlocks,
  generateToolbox,
  getBlocks,
};

// Register all common blocks
export function registerCommonBlocks() {
  registerBlocks();
}

// Add substitution statement block registration
function registerSubstitutionBlocks() {
  // Substitution statement block (e.g., $p;)
  const substitutionStatementId = `${PREFIX}_substitution_statement`;
  if (!Blockly.Blocks[substitutionStatementId]) {
    Blockly.Blocks[substitutionStatementId] = {
      init: function() {
        this.jsonInit({
          type: substitutionStatementId,
          message0: '📋 $ %1',
          args0: [
            {
              type: 'field_input',
              name: 'VARIABLE',
              text: 'p',
              validator: function(newValue) {
                // Allow variable names (letters, numbers, underscores, dots, and special OpenFOAM patterns)
                return newValue.match(/^[a-zA-Z_][a-zA-Z0-9_.:{}]*$/) ? newValue : null;
              }
            }
          ],
          previousStatement: null,
          nextStatement: null,
          colour: 230, // Light purple color for substitution
          tooltip: 'Substitution statement - inherit parameters from another block (e.g., $p copies all parameters from the p solver)',
          inputsInline: true
        });
      }
    };
  }
  
  console.log(`Registered substitution statement block: ${substitutionStatementId}`);
}
